<script setup lang="ts">
/**
 * 403. Доступ к проекту даёт членство, а приватный проект не виден и по
 * прямой ссылке — так что это штатный экран, а не аварийный.
 *
 * «Запросить доступ» просит администраторов проекта пустить (DEV-1493).
 * Повторное нажатие второго уведомления не поднимает — это решает сервис, —
 * поэтому кнопка после успеха просто уходит, оставляя строку «запрос отправлен».
 *
 * Ключ проекта приезжает в адресе: экран, с которого сюда увели, знает его, а
 * 403 сам по себе — нет. Без ключа кнопки нет: просить доступ не к чему.
 */
import { AppButton, useToast } from '@vinprop/design-system'
import { Eye } from 'lucide-vue-next'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { errorMessage } from '@/api/errors'
import TrackerEmptyState from '@/components/TrackerEmptyState.vue'
import { requestProjectAccess } from '@/features/projects/api'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const toast = useToast()

const projectKey = computed(() => String(route.query.key ?? ''))

const isAsking = ref(false)
const isAsked = ref(false)

const ask = async () => {
  isAsking.value = true

  try {
    await requestProjectAccess(projectKey.value)
    isAsked.value = true
  } catch (failure) {
    toast.error(errorMessage(failure, t('errors.forbidden.askFailed')))
  } finally {
    isAsking.value = false
  }
}
</script>

<template>
  <TrackerEmptyState
    :icon="Eye"
    :title="t('errors.forbidden.title')"
    :description="isAsked ? t('errors.forbidden.asked') : t('errors.forbidden.description')"
    tint="var(--color-warning-tint)"
  >
    <AppButton
      v-if="projectKey && !isAsked"
      variant="primary"
      :loading="isAsking"
      @click="ask"
    >
      {{ t('errors.forbidden.ask') }}
    </AppButton>

    <AppButton
      :variant="projectKey && !isAsked ? 'secondary' : 'primary'"
      @click="router.push('/my')"
    >
      {{ t('errors.back') }}
    </AppButton>
  </TrackerEmptyState>
</template>
